import React, { useState, useEffect, useContext, useCallback } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { AuthContext } from '../../context/AuthContext';
import { useInformativos } from '../../context/InformativosContext';
import InformativoForm from './components/InformativoForm';
import InformativoCard from './components/InformativoCard';
import CarouselBanner from './components/CarouselBanner';
import InformativosSidebar from './components/InformativosSidebar';
import MediaModal from './components/MediaModal';
import { PlusIcon } from '../../components/icons/Icons';
import './Informativos.css';

export default function Informativos() {
  const { user } = useContext(AuthContext);
  const { markAsViewed, unmarkAsViewed } = useInformativos();
  const [informativos, setInformativos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingInformativo, setEditingInformativo] = useState(null);
  const [showExpired, setShowExpired] = useState(false);
  const [viewingInformativo, setViewingInformativo] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  const fetchInformativos = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint =
        showExpired && user?.role === 'ADMIN_EAD' ? '/informativos/expired/list' : '/informativos';
      const res = await api.get(endpoint);
      const data = res.data.informativos || res.data || [];
      // For expired informativos, set isViewed to false since views are not included
      const processedData = showExpired ? data.map(inf => ({ ...inf, isViewed: false })) : data;
      setInformativos(processedData);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar informativos:', err);
      setError('Falha ao carregar informativos');
      toast.error('Erro ao carregar informativos');
    } finally {
      setLoading(false);
    }
  }, [showExpired, user]);

  // Buscar informativos
  useEffect(() => {
    fetchInformativos();
  }, [fetchInformativos]);

  const handleMarkAsViewed = async informativoId => {
    try {
      // Chama a função do contexto que já faz a API call e atualiza o contador
      await markAsViewed(informativoId);

      // Atualizar o estado local do informativo
      setInformativos(prev =>
        prev.map(inf => (inf.id === informativoId ? { ...inf, isViewed: true } : inf))
      );

      toast.success('Informativo marcado como visto!');
    } catch (err) {
      console.error('Erro ao marcar como visto:', err);
      toast.error('Erro ao marcar informativo como visto');
    }
  };

  const handleUnmarkAsViewed = async informativoId => {
    try {
      // Chama a função do contexto que já faz a API call e atualiza o contador
      await unmarkAsViewed(informativoId);

      // Atualizar o estado local do informativo
      setInformativos(prev =>
        prev.map(inf => (inf.id === informativoId ? { ...inf, isViewed: false } : inf))
      );

      toast.success('Informativo desmarcado como visto!');
    } catch (err) {
      console.error('Erro ao desmarcar como visto:', err);
      toast.error('Erro ao desmarcar informativo como visto');
    }
  };

  const handleOpenForm = (informativo = null) => {
    setEditingInformativo(informativo);
    setIsFormOpen(true);
    // Bloquear scroll do body
    document.body.style.overflow = 'hidden';
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingInformativo(null);
    // Liberar scroll do body
    document.body.style.overflow = '';
  };

  const handleViewInformativo = informativo => {
    if (informativo.images && informativo.images.length > 0) {
      setViewingInformativo(informativo);
      setSelectedMediaIndex(0);
      setShowMediaModal(true);
    }
  };

  const handleCloseView = () => {
    setShowMediaModal(false);
    setViewingInformativo(null);
    setSelectedMediaIndex(0);
  };

  const handleSaveInformativo = async data => {
    try {
      if (editingInformativo) {
        // Atualizar informativo existente
        const res = await api.put(`/informativos/${editingInformativo.id}`, data);
        setInformativos(prev =>
          prev.map(inf => (inf.id === editingInformativo.id ? res.data.informativo : inf))
        );
        toast.success('Informativo atualizado com sucesso!');
      } else {
        // Criar novo informativo
        const res = await api.post('/informativos', data);
        setInformativos(prev => [res.data.informativo, ...prev]);
        toast.success('Informativo criado com sucesso!');
      }
      handleCloseForm();
    } catch (err) {
      console.error('Erro ao salvar informativo:', err);
      toast.error(err.response?.data?.msg || 'Erro ao salvar informativo');
    }
  };

  const handleDeleteInformativo = async id => {
    if (!window.confirm('Tem certeza que deseja deletar este informativo?')) {
      return;
    }

    try {
      await api.delete(`/informativos/${id}`);
      setInformativos(prev => prev.filter(inf => inf.id !== id));
      toast.success('Informativo deletado com sucesso!');
    } catch (err) {
      console.error('Erro ao deletar informativo:', err);
      toast.error('Erro ao deletar informativo');
    }
  };

  if (loading) {
    return (
      <div className="informativos-page loading-state">
        <div className="loading-spinner"></div>
        <p>Carregando informativos...</p>
      </div>
    );
  }

  let emptyStateMessage;
  if (showExpired) {
    emptyStateMessage = 'Não há informativos expirados no momento.';
  } else if (user?.role === 'ADMIN_EAD') {
    emptyStateMessage = 'Crie seu primeiro informativo usando o botão flutuante';
  } else {
    emptyStateMessage = 'Volte mais tarde para ver novos informativos';
  }

  return (
    <div className="informativos-page">
      <div className="informativos-container">
        {/* Header */}
        <div className="informativos-header">
          <div>
            <h1 className="page-title">Informativos</h1>
            <p className="page-subtitle">Fique atualizado com os últimos informativos da empresa</p>
          </div>
        </div>

        {/* Filtros */}
        {user?.role === 'ADMIN_EAD' && (
          <div className="informativos-filters">
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={showExpired}
                onChange={e => setShowExpired(e.target.checked)}
              />
              <span className="toggle-slider"></span>
              Mostrar informativos expirados
            </label>
          </div>
        )}

        {/* Aviso de erro */}
        {error && (
          <div className="error-state">
            <p>{error}</p>
            <button className="btn btn-secondary" onClick={fetchInformativos}>
              Tentar Novamente
            </button>
          </div>
        )}

        {/* Banner de carrossel */}
        {!error && informativos.length > 0 && (
          <CarouselBanner informativos={informativos} onInformativoClick={handleViewInformativo} />
        )}

        {/* Divisor visual entre banner e cards */}
        {!error && informativos.length > 0 && <div className="banner-divider"></div>}

        {/* Lista de informativos ou estado vazio */}
        {informativos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📰</div>
            <h3>{showExpired ? 'Nenhum informativo expirado' : 'Nenhum informativo disponível'}</h3>
            <p>{emptyStateMessage}</p>
          </div>
        ) : (
          <div className="informativos-grid">
            {informativos.map(informativo => (
              <InformativoCard
                key={informativo.id}
                informativo={informativo}
                onEdit={() => handleOpenForm(informativo)}
                onDelete={() => handleDeleteInformativo(informativo.id)}
                onMarkAsViewed={handleMarkAsViewed}
                onUnmarkAsViewed={handleUnmarkAsViewed}
                isViewed={informativo.isViewed}
                canEdit={user?.role === 'ADMIN_EAD'}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal de formulário */}
      {isFormOpen && (
        <InformativoForm
          informativo={editingInformativo}
          onClose={handleCloseForm}
          onSave={handleSaveInformativo}
        />
      )}

      {/* Sidebar de informativos não vistos */}
      <InformativosSidebar />

      {/* Modal de mídia para banner */}
      {showMediaModal && viewingInformativo && (
        <MediaModal
          medias={viewingInformativo.images || []}
          initialIndex={selectedMediaIndex}
          onClose={handleCloseView}
        />
      )}

      {/* Floating Action Button */}
      {user?.role === 'ADMIN_EAD' && (
        <button className="fab" onClick={() => handleOpenForm()}>
          <PlusIcon />
        </button>
      )}
    </div>
  );
}
